<template>
  <div class="glasses-width-slider flex min-h-[70px] flex-col gap-6">
    <div>
      <FimButton
        type="raw"
        no-padding
        is-small
        data-tracking-label="Brillenbreite - Info"
        class="text-sm"
        @click="openOverlay"
      >
        <template #icon>
          <LazyIconFielmannInfoCircle class="size-4" />
        </template>
        {{ $t('filter.more_info') }}
      </FimButton>
    </div>
    <VueSlider
      v-model="range"
      :enable-cross="false"
      :contained="true"
      adsorb
      tooltip="none"
      :min="min"
      :max="max"
      :rail-style="{
        background: '#F5F7FA',
        height: '4px',
        border: '1px solid #D7D7D9',
      }"
    >
      <template #dot>
        <button
          class="size-6 shrink-0 cursor-pointer self-center rounded-full border bg-white shadow focus:outline-none"
        ></button>
      </template>
    </VueSlider>
    <div class="flex items-center justify-between gap-4 text-lg text-gray-700">
      <label class="rounded border border-neutral-30 bg-neutral-10 px-2 py-1">
        <input
          v-model="userMin"
          class="appearance-none border-none bg-transparent p-0"
          type="number"
          :min="min"
          :max="max"
          @change="onUserManualInput"
        />
        <span class="ml-1 select-none opacity-40">mm</span>
      </label>
      <label class="rounded border border-neutral-30 bg-neutral-10 px-2 py-1">
        <input
          v-model="userMax"
          class="appearance-none border-none bg-transparent p-0"
          type="number"
          :min="min"
          :max="max"
          @change="onUserManualInput"
        />
        <span class="ml-1 select-none opacity-40">mm</span>
      </label>
    </div>
    <Overlay
      :open="isGlassesWidthFilterOverlayOpen"
      :show-title="false"
      portal-target="body"
    >
      <h3 class="mb-4 w-full text-center text-lg font-medium">
        {{ $t('filter.glasses_width.popup.title') }}
      </h3>
      <div
        class="m-auto flex w-[253px] flex-col items-center justify-center text-center sm:w-max"
      >
        <NuxtImg
          provider="local"
          src="/img/filters/glasses.png"
          :alt="$t('filter.glasses_width.popup.text')"
          loading="lazy"
          width="253"
          height="112"
        />
        <div
          class="-mt-12 flex w-[calc(100%-1rem)] justify-center border border-t-0 border-black pt-12"
        >
          <span class="mb-[-0.9rem] block bg-white px-4 text-lg">{{
            $t('filter.glasses_width.popup.label')
          }}</span>
        </div>
      </div>
      <div class="my-8 text-center leading-6 text-[#979797]">
        {{ $t('filter.glasses_width.popup.text') }}
      </div>
      <form class="flex flex-col gap-2" @submit.prevent="onUserInput">
        <input
          v-model="userInput"
          class="border-1-[#E6E6E7] rounded border p-2 text-center text-lg leading-[1.333]"
          :placeholder="$t('filter.glasses_width.popup.placeholder')"
          type="number"
          :min="min"
          :max="max"
        />
        <FimButton html-type="submit" type="primary">{{
          $t('filter.glasses_width.popup.confirm')
        }}</FimButton>
        <FimButton
          html-type="reset"
          type="link-alternative"
          @click="isGlassesWidthFilterOverlayOpen = false"
          >{{ $t('filter.glasses_width.popup.cancel') }}</FimButton
        >
      </form>
    </Overlay>
  </div>
</template>

<script setup lang="ts">
// @ts-expect-error no type definitions
import VueSlider from 'vue-slider-component/dist-css/vue-slider-component.umd.min.js'
import 'vue-slider-component/dist-css/vue-slider-component.css'
type RangeTuple = [number, number]

interface Props {
  modelValue: RangeTuple
  min?: number
  max?: number
  data: any[] | undefined
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [3, 29999],
  min: 0,
  max: 100000,
  data: undefined,
})

const userMin = ref(props.modelValue[0])
const userMax = ref(props.modelValue[1])

const emit = defineEmits(['input', 'update:model-value'])
const range = computed({
  get: (): RangeTuple => props.modelValue,
  set: (newValue: RangeTuple) => {
    emit('update:model-value', newValue)
    userMin.value = newValue[0]
    userMax.value = newValue[1]
  },
})

const userInput = ref('')
const isGlassesWidthFilterOverlayOpen = ref(false)

function findClosestFilterValue(
  inputNumber: number,
  values: any[] | undefined,
): number | undefined {
  if (!values || values.length === 0) {
    return
  }

  let closestItem = null
  let minDifference = Math.abs(
    inputNumber - parseInt(values[0].displayName, 10),
  )

  for (let i = 1; i < values.length; i++) {
    const currentDifference = Math.abs(
      inputNumber - parseInt(values[i].displayName, 10),
    )
    if (currentDifference < minDifference) {
      closestItem = values[i]
      minDifference = currentDifference
    }
  }

  return parseInt(closestItem.displayName, 10)
}

// When user inputs a number manually, find the filter value with closest number
// and set a range of +/-2mm around it
const RANGE_MARGIN = 2
const onUserInput = () => {
  const closestValue = findClosestFilterValue(
    parseInt(userInput.value, 10),
    props.data,
  )
  if (closestValue) {
    const newMin = Math.max(props.min, closestValue - RANGE_MARGIN)
    const newMax = Math.min(closestValue + RANGE_MARGIN, props.max)
    range.value = [newMin, newMax]
    userMin.value = newMin
    userMax.value = newMax
  }
  isGlassesWidthFilterOverlayOpen.value = false
  userInput.value = ''
}

// When user inputs a number manually, find the filter value with closest number
// and set a range of +/-2mm around it
const onUserManualInput = () => {
  // make sure to order the values correctly and clamp them between min and max
  const min = Math.min(userMin.value, userMax.value)
  const max = Math.max(userMin.value, userMax.value)

  const cmin = min > props.min && min < props.max ? min : props.min
  const cmax = max > props.min && max < props.max ? max : props.max

  range.value = [cmin, cmax]
}

const { trackButtonClick } = await useTrackingEvents()

const openOverlay = (e: any) => {
  trackButtonClick(e.target)
  isGlassesWidthFilterOverlayOpen.value = true
}
</script>

<style scoped lang="scss">
$themeColor: #dcdee0;

input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  appearance: none;
  margin: 0;
}

.glasses-width-slider :deep(.vue-slider-dot) {
  display: flex;
}
</style>
